/**
 * Ce qui quitte Kollectivia vers un assureur — chunk 9.BK.3.
 *
 * ## La règle, et pourquoi elle prend cette forme
 *
 * Éric envoie aujourd'hui le classeur **tel quel**, toutes colonnes comprises :
 * l'arbitrage métier est donc « tout sort ». Ce fichier serait alors inutile…
 * si la règle était « ce qui sort ». Elle est autre :
 *
 * > **Aucune donnée ajoutée plus tard au modèle ne devient automatiquement
 * > transmissible.**
 *
 * Une simple liste des colonnes exportées ne le garantit pas : elle protège
 * tant que quelqu'un pense à la relire. D'où **deux listes**, et un test qui
 * vérifie que leur union est **exactement** le modèle.
 *
 * Ajouter un champ au census fait alors **rougir la suite** tant que personne ne
 * l'a rangé dans l'une des deux. Le défaut devient « refusé », pas « oublié ».
 *
 * ## Ce qui ne peut pas sortir, par construction
 *
 * `import_raw`, `client_row_id`, `tenant_id`, `census_id`, les horodatages,
 * `deleted_at` : ce ne sont pas des champs du modèle métier. Ils n'apparaissent
 * dans aucune des deux listes et **aucun chemin ne les projette** — l'export lit
 * `COLONNES_EXPORT_ASSUREUR`, pas la ligne.
 *
 * ## La portée exacte : le modèle PARTICIPANT v1, pas « toutes les tables »
 *
 * Cette projection couvre **tous les champs du modèle participant v1 confirmé
 * par GFS** — et **pas** `app.census_dependents`. Ce n'est pas un oubli, et il
 * faut le dire précisément parce que la table existe et que la face publique
 * sait l'écrire.
 *
 * **Décision métier du 2026-09-13, confirmée par Éric** : la feuille des
 * personnes à charge **n'est pas utilisée** dans ce flux, il ne les collecte
 * pas, 9.BK.2 ne les importe pas, et aucune règle de complétude v1 n'en dépend.
 * Les projeter reviendrait à transmettre une population que le cabinet ne
 * collecte pas — donc, au mieux, des colonnes vides chez l'assureur.
 *
 * **Relevé par la revue Codex de la PR #138** comme un défaut, et c'en serait
 * un le jour où le produit PAC sera activé : l'empreinte de fraîcheur ignore
 * elle aussi les personnes à charge, si bien qu'en ajouter une ne périmerait
 * pas l'export. **Cette décision devra donc être explicitement revisitée** à ce
 * moment-là — ce n'est pas un choix d'architecture, c'est un choix de périmètre
 * produit v1.
 */

import type { CensusParticipantInput } from "./census.js";

/** Un champ du modèle. Le type empêche d'inventer un nom de colonne. */
type ChampParticipant = keyof CensusParticipantInput;

/**
 * Les colonnes transmises, **dans l'ordre du classeur du pilote**.
 *
 * Les en-têtes reproduisent ceux du gabarit qu'Éric envoie déjà : un
 * souscripteur qui reçoit ce fichier doit le lire comme il lit les autres, et
 * un fichier réimporté dans Kollectivia doit se reconnaître lui-même.
 */
export const COLONNES_EXPORT_ASSUREUR: ReadonlyArray<{
  readonly champ: ChampParticipant;
  readonly entete: string;
}> = [
  { champ: "employee_number", entete: "Numéro unique" },
  { champ: "first_name", entete: "Prénom" },
  { champ: "last_name", entete: "Nom de famille" },
  { champ: "mobile_phone", entete: "Numéro de cellulaire" },
  { champ: "email", entete: "Courriel" },
  { champ: "birth_date", entete: "Date de naissance" },
  { champ: "gender", entete: "Sexe" },
  { champ: "hire_date", entete: "Date d'embauche" },
  { champ: "hours_per_week", entete: "Heures travail / semaine" },
  { champ: "smoker", entete: "Fumeur" },
  { champ: "additional_email", entete: "Adresse courriel additionnelle" },
  { champ: "additional_phone", entete: "Numéro de téléphone additionnel" },
  { champ: "note", entete: "Note" },
  { champ: "coverage_start", entete: "Date de début (couverture)" },
  { champ: "coverage_end", entete: "Date de fin (couverture)" },
  { champ: "employment_status", entete: "Statut" },
  { champ: "coverage", entete: "Couverture" },
  { champ: "division", entete: "Division" },
  { champ: "occupation", entete: "Occupation" },
  { champ: "annual_salary", entete: "Salaire annuel" },
  { champ: "hourly_rate", entete: "Taux horaire" },
  { champ: "employment_type", entete: "Temps plein/temps partiel/Saisonnier" },
];

/**
 * Les champs du modèle **délibérément retenus**, avec leur raison.
 *
 * **Vide pour GFS v1** — Éric transmet tout, et l'inventer autrement serait
 * décider à sa place. La liste existe quand même : c'est elle qui rend le test
 * d'exhaustivité capable de dire « ce champ n'a pas été classé » plutôt que
 * « ce champ n'est pas exporté ».
 *
 * Le jour où un assureur demande moins — ou où la minimisation l'impose — c'est
 * ici que la décision s'écrit, avec son motif à côté.
 */
export const CHAMPS_JAMAIS_EXPORTES: ReadonlyArray<{
  readonly champ: ChampParticipant;
  readonly raison: string;
}> = [];

/** Les champs exportés, seuls, pour les consommateurs qui n'ont besoin que d'eux. */
export const CHAMPS_EXPORTES: readonly ChampParticipant[] = COLONNES_EXPORT_ASSUREUR.map(
  (c) => c.champ,
);
